import { Notification, NotificationGroup } from "../types";

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export class NotificationGrouper {
  private locale: string;
  private weekStartsOn: 0 | 1;
  private now: Date;
  private keyOf: (it: Notification) => string;
  private fmtWeekday: Intl.DateTimeFormat;
  private fmtShortMD: Intl.DateTimeFormat;
  private fmtMonthYYYY: Intl.DateTimeFormat;

  constructor(opts?: {
    locale?: string;
    weekStartsOn?: 0 | 1;
    now?: Date;
    keyOf?: (it: Notification) => string;
  }) {
    this.locale = opts?.locale ?? "en-US";
    this.weekStartsOn = opts?.weekStartsOn ?? 1;
    this.now = opts?.now ?? new Date();
    this.keyOf =
      opts?.keyOf ??
      ((it) => it.id ?? `${it.created_at}|${it.type}|${it.user?.id ?? ""}`);
    this.fmtWeekday = new Intl.DateTimeFormat(this.locale, { weekday: "long" });
    this.fmtShortMD = new Intl.DateTimeFormat(this.locale, {
      month: "short",
      day: "2-digit"
    });
    this.fmtMonthYYYY = new Intl.DateTimeFormat(this.locale, {
      month: "long",
      year: "numeric"
    });
  }

  private startOfWeek(d: Date) {
    const s = startOfDay(d);
    const diff = (s.getDay() - this.weekStartsOn + 7) % 7;
    return addDays(s, -diff);
  }

  private endOfWeek(d: Date) {
    return endOfDay(addDays(this.startOfWeek(d), 6));
  }

  private weekdayShort(d: Date) {
    return `${this.fmtWeekday.format(d)} - ${this.fmtShortMD.format(d)}`;
  }

  /* eslint-disable sonarjs/cognitive-complexity -- it's a complex function */
  group(items: Notification[]): NotificationGroup[] {
    const parsed = items
      .map((it) => ({ ...it, __d: new Date(it.created_at) }))
      .sort((a, b) => b.__d.getTime() - a.__d.getTime());

    const sections: NotificationGroup[] = [];
    const assigned = new Set<Notification>();

    const todayStart = startOfDay(this.now);
    const todayEnd = endOfDay(this.now);
    const yesterdayStart = startOfDay(addDays(this.now, -1));
    const yesterdayEnd = endOfDay(addDays(this.now, -1));
    const thisWeekStart = this.startOfWeek(this.now);
    const thisWeekEnd = this.endOfWeek(this.now);
    const lastWeekStart = this.startOfWeek(addDays(this.now, -7));
    const lastWeekEnd = endOfDay(addDays(this.startOfWeek(this.now), -1));

    const pick = (from: Date, to: Date, labelFn?: (d: Date) => string) => {
      const out: (Notification & { label?: string })[] = [];
      for (const it of parsed) {
        if (assigned.has(it)) continue;
        const t = it.__d.getTime();
        if (t >= from.getTime() && t <= to.getTime()) {
          const label = labelFn?.(it.__d);
          out.push(label ? { ...it, label } : it);
          assigned.add(it);
        }
      }
      return out;
    };

    const todayItems = pick(todayStart, todayEnd);
    if (todayItems.length) sections.push({ name: "Today", items: todayItems });

    const yesterdayItems = pick(yesterdayStart, yesterdayEnd);
    if (yesterdayItems.length)
      sections.push({ name: "Yesterday", items: yesterdayItems });

    const thisWeekItems = pick(thisWeekStart, thisWeekEnd, (d) =>
      this.weekdayShort(d)
    );
    if (thisWeekItems.length) {
      const byLabel = new Map<string, (Notification & { label?: string })[]>();
      for (const it of thisWeekItems) {
        if (!it.label) continue;
        const arr = byLabel.get(it.label) ?? [];
        arr.push(it);
        byLabel.set(it.label, arr);
      }
      for (const [label, arr] of byLabel.entries())
        sections.push({ name: label, items: arr });
    }

    const lastWeekItems = pick(lastWeekStart, lastWeekEnd);
    if (lastWeekItems.length)
      sections.push({ name: "Last Week", items: lastWeekItems });

    const older: (Notification & { label?: string })[] = [];
    for (const it of parsed) {
      if (assigned.has(it)) continue;
      older.push({ ...it, label: this.fmtMonthYYYY.format(it.__d) });
      assigned.add(it);
    }
    if (older.length) {
      const byMonth = new Map<string, (Notification & { label?: string })[]>();
      for (const it of older) {
        if (!it.label) continue;
        const arr = byMonth.get(it.label) ?? [];
        arr.push(it);
        byMonth.set(it.label, arr);
      }
      for (const [label, arr] of byMonth.entries())
        sections.push({ name: label, items: arr });
    }

    return sections;
  }

  merge(
    current: NotificationGroup[],
    newItems: Notification[]
  ): NotificationGroup[] {
    if (!newItems.length) return current;
    const incoming = this.group(newItems);
    const byName = new Map(
      current.map((s) => [s.name, { ...s, items: [...s.items] }])
    );
    for (const inc of incoming) {
      const cur = byName.get(inc.name) ?? {
        name: inc.name,
        items: [] as (Notification & { label?: string })[]
      };
      const pool = [...cur.items, ...inc.items];
      const seen = new Set<string>();
      const dedup: (Notification & { label?: string })[] = [];
      for (const it of pool) {
        const k = this.keyOf(it);
        if (seen.has(k)) continue;
        seen.add(k);
        dedup.push(it);
      }
      dedup.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      cur.items = dedup;
      byName.set(inc.name, cur);
    }
    return [...byName.values()];
  }
}
